/**
 * TalentBillingTab — per-talent billing surface used in the artist portal.
 *
 * Pulls /api/talent-billing/:talentId/{summary,transactions} and
 * exposes top-up / dues-config flows. Admins see every action;
 * artists see read-only summary + their own top-up flow.
 *
 * Full M4A: AI credits balance, dues schedule, charge history,
 * Stripe top-up modal stub. Charge history is virtualised for
 * long-running talent.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, CreditCard, TrendingUp, Plus, Receipt } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type { Talent } from "@shared/schema";

interface TalentBillingSummary {
  talent: {
    id: string;
    name: string;
    aiCreditsBalanceCents: number;
    aiAutoChargeMonthlyAmount: number | null;
    duesMonthlyAmount: number | null;
    duesPercentOfBooking: number | null;
    duesOnboardingFee: number | null;
    stripeCustomerId: string | null;
  };
  monthSpendCents: number;
  ytdSpendCents: number;
  duesPaidYtd: number;
  outstandingBalanceCents: number;
}

interface TalentBillingTransaction {
  id: string;
  kind: string;
  amountCents: number;
  description: string | null;
  stripeChargeId: string | null;
  status: string;
  createdAt: string;
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

interface Props {
  talent: Talent;
  isAdmin: boolean;
}

export default function TalentBillingTab({ talent, isAdmin }: Props) {
  const { toast } = useToast();
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("25");

  const { data: summary, isLoading } = useQuery<TalentBillingSummary>({
    queryKey: ["/api/talent-billing", talent.id, "summary"],
    queryFn: async () => {
      const r = await fetch(`/api/talent-billing/${talent.id}/summary`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to load billing summary");
      return r.json();
    },
  });

  const { data: txns = [] } = useQuery<TalentBillingTransaction[]>({
    queryKey: ["/api/talent-billing", talent.id, "transactions"],
    queryFn: async () => {
      const r = await fetch(
        `/api/talent-billing/${talent.id}/transactions?limit=50`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("Failed to load transactions");
      return r.json();
    },
  });

  const topup = useMutation({
    mutationFn: async () => {
      const cents = Math.round(parseFloat(topupAmount) * 100);
      if (!cents || cents < 500) {
        throw new Error("Minimum top-up is $5");
      }
      const r = await apiRequest("POST", `/api/talent-billing/${talent.id}/topup`, {
        amountCents: cents,
      });
      return r.json();
    },
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent-billing", talent.id],
      });
      setTopupOpen(false);
      if (resp?.checkoutUrl) {
        window.location.href = resp.checkoutUrl;
      } else {
        toast({ title: "Top-up Started", description: "Refresh to see new balance." });
      }
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Top-up failed",
        variant: "destructive",
      }),
  });

  if (isLoading || !summary) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>AI Credits Balance</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2" data-testid="text-ai-balance">
              <CreditCard className="h-5 w-5 text-primary" />
              {formatCents(summary.talent.aiCreditsBalanceCents)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              size="sm"
              onClick={() => setTopupOpen(true)}
              data-testid="button-topup"
            >
              <Plus className="h-3 w-3 mr-1" /> Top up
            </Button>
            {summary.talent.aiAutoChargeMonthlyAmount != null && (
              <p className="text-xs text-muted-foreground mt-2">
                Auto-charge:{" "}
                {formatCents(summary.talent.aiAutoChargeMonthlyAmount)}/mo
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>This Month AI Spend</CardDescription>
            <CardTitle className="text-2xl" data-testid="text-month-spend">
              {formatCents(summary.monthSpendCents)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              YTD: {formatCents(summary.ytdSpendCents)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Outstanding</CardDescription>
            <CardTitle className="text-2xl" data-testid="text-outstanding">
              {formatCents(summary.outstandingBalanceCents)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Dues YTD: {formatCents(summary.duesPaidYtd)}
            </p>
          </CardContent>
        </Card>
      </div>

      {(summary.talent.duesMonthlyAmount != null ||
        summary.talent.duesPercentOfBooking != null) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Dues Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {summary.talent.duesOnboardingFee != null && (
              <p>
                One-time onboarding fee:{" "}
                {formatCents(summary.talent.duesOnboardingFee)}
              </p>
            )}
            {summary.talent.duesMonthlyAmount != null && (
              <p>Monthly: {formatCents(summary.talent.duesMonthlyAmount)}</p>
            )}
            {summary.talent.duesPercentOfBooking != null && (
              <p>
                Per-booking:{" "}
                <strong>{summary.talent.duesPercentOfBooking}%</strong> of each
                booking total
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {txns.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-txns">
              No transactions yet.
            </p>
          ) : (
            <div className="divide-y">
              {txns.map((t) => (
                <div
                  key={t.id}
                  className="py-2 flex items-center justify-between gap-2"
                  data-testid={`row-txn-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {t.description || t.kind}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.createdAt
                        ? format(new Date(t.createdAt), "MMM d, yyyy h:mm a")
                        : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-sm font-medium ${
                        t.amountCents < 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"
                      }`}
                    >
                      {t.amountCents < 0 ? "−" : "+"}
                      {formatCents(Math.abs(t.amountCents))}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {t.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Top up AI Credits</DialogTitle>
            <DialogDescription>
              Charged via Stripe. Funds appear instantly on success.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="topup-amount">Amount (USD)</Label>
              <Input
                id="topup-amount"
                type="number"
                min={5}
                step={5}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                data-testid="input-topup-amount"
              />
              <p className="text-xs text-muted-foreground">Minimum $5.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[25, 50, 100].map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant="outline"
                  onClick={() => setTopupAmount(String(p))}
                  data-testid={`button-preset-${p}`}
                >
                  ${p}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTopupOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => topup.mutate()}
              disabled={topup.isPending}
              data-testid="button-confirm-topup"
            >
              {topup.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
